import React, { useEffect, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Spinner,
} from "reactstrap"
import { Form, ErrorMessage, Field, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import Select from "react-select";
import { getAllGiftCardCategories } from "store/actions/giftCardCategoriesAction";
import {
  addAllergenSchema,
  addGiftCardSchema,
  addVehicleSchema,
} from "components/Common/validation"
import {
  addAllergen,
  getAllergenDetails,
  updateAllergen,
} from "store/actions/allergensAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import {
  getImagePreviewSrc,
  getImagePreviewSrc2,
  handleFileChange,
  handleFileChange2,
  handleFileChange3,
} from "components/Shared/fileHandlers"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import {
  addVehicle,
  getVehicleDetails,
  updateVehicle,
} from "store/actions/vehicleAction"
import {
  addGiftCard,
  getGiftCardDetails,
  updateGiftCard,
} from "store/actions/giftCardAction"
import { getAllCustomersList } from "store/actions/customersAction"
import Switch from "components/Shared/Switch"
import { DatePicker } from "rsuite"
import { isBefore, startOfDay } from "date-fns"
import {
  categoryHeight,
  categoryMaxSize,
  categoryWidth,
  giftCardSize,
} from "components/Common/ImageSize"
import addGiftCardUploadTemplate from "assets/file/addGiftCardUploadTemplate.csv"

const initialValues = {
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
  // startsAt: "",
  // expiresAt: "",
  amount: null,
  cardType: null,
  // validityInDays: 365,
  image: "",
  isLimitToSpecificCustomer: false,
  customers: [],
  file: "",
}

const AddGiftCard = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Gift Card "
  )}`

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, giftCardDetails } = useSelector(
    state => state.GiftCard
  )
  const { categories } = useSelector(state => state.GiftCardCategories)
  const { customersDropdownList } = useSelector(state => state.Customers)
  const { userRole, accountType } = useSelector(state => state.Login)

  const [formValues, setFormValues] = useState(initialValues)
  const [customerOptions, setCustomerOptions] = useState([])

  const fetchCustomersList = keyword => {
    dispatch(getAllCustomersList({ keyword }))
  }

  useEffect(() => {
    if (customersDropdownList?.length > 0) {
      const updatedCustomerOption = customersDropdownList?.map(item => ({
        label: `${item.firstName} ${item.lastName}${
          item?.phoneNumber ? `, ${item.phoneNumber}` : ""
        }`,
        value: item._id,
      }))
      setCustomerOptions(updatedCustomerOption)
    }
  }, [customersDropdownList])

  const categoryOptions = categories?.map(category => ({
    value: category._id,
    label: category.cardType
  })) || [];

 useEffect(() => {
     // Fetch categories when component mounts
     dispatch(getAllGiftCardCategories({
       perPage: 100, // Get all categories
       pageNo: 1,
       active: null,
       orderBy: 1,
       keyword: "",
     }));
   }, []);

  useEffect(() => {
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      37,
      "gift_card_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      37,
      "gift_card_3",
      accountType
    )

    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    if (mode === "edit") {
      getOldValues()
    }
  }, [])

  const getOldValues = () => {
    dispatch(getGiftCardDetails(id))
  }

  useEffect(() => {
    if (giftCardDetails && !loading && mode === "edit") {
      const {
        name,
        nameAr,
        description,
        descriptionAr,
        // startsAt,
        // expiresAt,
        amount,
        // validityInDays,
        imageUrl,
        cardType,
        isLimitToSpecificCustomer,
        customers,
      } = giftCardDetails

      const updatedCustomers = customers?.map(item => ({
        label: `${item.firstName} ${item.lastName}`,
        value: item._id,
      }))

      setFormValues({
        ...formValues,
        name,
        nameAr,
        description,
        descriptionAr,
        // startsAt: new Date(startsAt),
        // expiresAt: new Date(expiresAt),
        amount,
        cardType,
        // validityInDays,
        image: imageUrl,
        isLimitToSpecificCustomer: isLimitToSpecificCustomer || false,
        customers: updatedCustomers || [],
      })
    }
  }, [giftCardDetails])

  const handleOnSubmit = values => {
    const {
      name,
      nameAr,
      description,
      descriptionAr,
      // startsAt,
      // expiresAt,
      amount,
      // validityInDays,
      image,
      cardType,
      isLimitToSpecificCustomer,
      customers,
      file,
    } = values

    let data = new FormData()
    data.append("name", name)
    data.append("nameAr", nameAr)
    data.append("description", description)
    data.append("descriptionAr", descriptionAr)
    data.append("cardType", cardType)
    // data.append("startsAt", startsAt)
    // data.append("expiresAt", expiresAt)
    data.append("amount", amount)
    // data.append("validityInDays", validityInDays)
    data.append("image", image)
    data.append("isLimitToSpecificCustomer", isLimitToSpecificCustomer)

    if (customers?.length === 0) {
      data.append("customers[]", [])
    } else {
      customers?.forEach((element, index) => {
        data.append(`customers[${index}]`, element.value)
      })
    }

    if (file && file instanceof File) {
      data.append("file", file)
    }

    if (mode === "add") {
      dispatch(addGiftCard(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      data.append("id", id)
      dispatch(updateGiftCard(data))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }


  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Gift Card")}
                </CardTitle>
                <Formik
                  initialValues={formValues}
                  enableReinitialize={true}
                  validationSchema={addGiftCardSchema()}
                  onSubmit={handleOnSubmit}
                >
                  {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                      <div className="row">
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="name">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(English)")}
                            </label>
                            <Field
                              type="text"
                              id="name"
                              name="name"
                              placeholder={t("Name(English)")}
                              className={`form-control ${
                                touched.name && errors.name ? "is-invalid" : ""
                              }`}
                            />
                            <TextError name="name" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="nameAr">
                              <span className="text-danger">*</span>{" "}
                              {t("Name(عربي)")}
                            </label>
                            <Field
                              type="text"
                              style={{ direction: "rtl" }}
                              id="nameAr"
                              name="nameAr"
                              placeholder={t("Name(عربي)")}
                              className={`form-control ${
                                touched.nameAr && errors.nameAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="nameAr" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="description">
                              {t("Description(English)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              id="description"
                              name="description"
                              placeholder={t("Description(English)")}
                              className={`form-control ${
                                touched.description && errors.description
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="descriptionAr">
                              {t("Description(عربي)")}
                            </label>
                            <Field
                              as="textarea"
                              rows={3}
                              style={{ direction: "rtl" }}
                              id="descriptionAr"
                              name="descriptionAr"
                              placeholder={t("Description(عربي)")}
                              className={`form-control ${
                                touched.descriptionAr && errors.descriptionAr
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="descriptionAr" />
                          </div>
                        </div>
                       
                        {/* <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="endTime">
                              <span className="text-danger">*</span>{" "}
                              {t("Start Date & Time")}
                            </label>
                            <DatePicker
                              showTime={{ format: "HH:mm" }} // Show time picker
                              format="yyyy-MM-dd HH:mm" // Custom format for displaying date and time
                              onChange={value => {
                                setFieldValue("startsAt", value)
                              }}
                              value={values.startsAt}
                              shouldDisableDate={date =>
                                isBefore(
                                  startOfDay(date),
                                  startOfDay(new Date())
                                )
                              }
                              placement="autoVerticalStart"
                              placeholder={t("Start Date & Time")}
                              className={`date_picker ${
                                errors.startsAt && touched.startsAt
                                  ? "invalid"
                                  : ""
                              }`}
                            />

                            <TextError name="startsAt" />
                          </div>
                        </div>
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="endTime">
                              <span className="text-danger">*</span>{" "}
                              {t("End Date & Time")}
                            </label>
                            <DatePicker
                              showTime={{ format: "HH:mm" }}
                              format="yyyy-MM-dd HH:mm"
                              onChange={value => {
                                setFieldValue("expiresAt", value)
                              }}
                              value={values.expiresAt}
                              shouldDisableDate={date =>
                                isBefore(
                                  startOfDay(date),
                                  startOfDay(new Date())
                                )
                              }
                              placement="autoVerticalStart"
                              placeholder={t("End Date & Time")}
                              className={`date_picker ${
                                errors.expiresAt && touched.expiresAt
                                  ? "invalid"
                                  : ""
                              }`}
                            />

                            <TextError name="expiresAt" />
                          </div>
                        </div> */}
                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="amount">
                              <span className="text-danger">*</span>{" "}
                              {t("Amount")}
                            </label>
                            <Field
                              type="number"
                              min="1"
                              id="amount"
                              name="amount"
                              placeholder={t("Amount")}
                              className={`form-control ${
                                touched.amount && errors.amount
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="amount" />
                          </div>
                        </div>
                        {/* <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="validityInDays">
                              <span className="text-danger">*</span>{" "}
                              {t("Eidiyah Redemption Validity In Days")}
                            </label>
                            <Field
                              type="number"
                              min="1"
                              id="validityInDays"
                              name="validityInDays"
                              placeholder={t(
                                "Eidiyah Redemption Validity In Days"
                              )}
                              className={`form-control ${
                                touched.validityInDays && errors.validityInDays
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError name="validityInDays" />
                          </div>
                        </div> */}

                        <div className="col-lg-6">
                          <div className="form_field position-relative">
                            <label htmlFor="image">
                              <span className="text-danger">*</span>{" "}
                              {t("Upload Image")}
                            </label>
                            <input
                              type="file"
                              id="image"
                              name="image"
                              accept="image/jpeg, image/jpg, image/png"
                              onChange={e => {
                                handleFileChange3(
                                  e,
                                  "image",
                                  setFieldValue,
                                  giftCardSize
                                )
                              }}
                              className={`form-control ${
                                touched.image && errors.image
                                  ? "is-invalid"
                                  : ""
                              }`}
                            />
                            <TextError
                              name="image"
                              component="div"
                              className="text-danger"
                            />
                          </div>
                          <span>
                            {t(
                              "Recommended dimensions: Size should be less than or equal to {{size}} KB",
                              {
                                size: giftCardSize,
                              }
                            )}
                          </span>
                          {getImagePreviewSrc2(values.image) && (
                            <div className="mt-3">
                              <img
                                src={getImagePreviewSrc2(values.image)}
                                alt="Preview"
                                className="img_preview"
                              />
                            </div>
                          )}
                        </div>

                        <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="category">
                              <span className="text-danger">*</span>{" "}
                              {t("Category")}
                            </label>
                            <Select
                              name="cardType"
                              value={categoryOptions?.find(
                                ({ value }) => value == values?.cardType
                              )}
                              onChange={({ value }) =>
                                setFieldValue("cardType", value)
                              }
                              options={categoryOptions}
                              placeholder={t("Select Category")}
                              className={`select2-selection ${
                                touched.cardType && errors.cardType
                                  ? "danger_color_border"
                                  : ""
                              }`}
                              classNamePrefix="select2"
                              isSearchable
                              styles={{
                                control: base => ({
                                  ...base,
                                  minHeight: "38px",
                                  height: "38px",
                                }),
                                valueContainer: base => ({
                                  ...base,
                                  height: "38px",
                                  padding: "0 8px",
                                }),
                                input: base => ({
                                  ...base,
                                  margin: "0px",
                                }),
                                indicatorsContainer: base => ({
                                  ...base,
                                  height: "38px",
                                }),
                              }}
                            />
                            <TextError name="cardType" />
                            <Field type="hidden" name="category" />
                          </div>
                        </div>

                        {mode !== "edit" && (
                          <div className="col-lg-6">
                          <div className="form_field">
                            <label htmlFor="isLimitToSpecificCustomer">
                              {t("Limit To Specific Customers")}
                            </label>
                            <Switch
                              isChecked={values.isLimitToSpecificCustomer}
                              onClickHandler={() => {
                                setFieldValue(
                                  "isLimitToSpecificCustomer",
                                  !values.isLimitToSpecificCustomer
                                )
                              }}
                            />
                          </div>
                        </div>
                        )}

                        {mode !== "edit" && values.isLimitToSpecificCustomer && (
                          <>
                            <div className="col-lg-6 mt-2">
                              <label htmlFor="">
                                {t("Please Type To Search Customer")}
                              </label>
                              <Select
                                value={values.customers}
                                isMulti={true}
                                onChange={selectedValue => {
                                  setFieldValue("customers", selectedValue)
                                }}
                                options={customerOptions?.map(item => ({
                                  label: item.label,
                                  value: item.value,
                                }))}
                                onInputChange={value =>
                                  fetchCustomersList(value)
                                }
                                placeholder={t(
                                  "Please Type To Search Customer"
                                )}
                                className={`select2-selection ${
                                  touched.customers && errors.customers
                                    ? "danger_color_border"
                                    : ""
                                }`}
                              />
                              <TextError name="customers" />
                            </div>
                            <div className="col-lg-3 mt-2">
                              <div className="form_field">
                                <label htmlFor="file">
                                  {t("Upload Customers via CSV")}
                                </label>
                                <input
                                  type="file"
                                  id="file"
                                  name="file"
                                  accept=".csv"
                                  onChange={e => {
                                    setFieldValue(
                                      "file",
                                      e.currentTarget.files[0] || ""
                                    )
                                  }}
                                  className="form-control"
                                />
                                <small className="text-muted d-block mt-1">
                                  {t(
                                    "Upload a CSV file with phone numbers to send gift cards to specific customers"
                                  )}
                                </small>
                                <TextError name="file" />
                              </div>
                            </div>
                            <div className="col-lg-3 mt-2">
                              <div style={{ marginTop: 36 }}>
                                <Button
                                  color="primary"
                                  className="ms-3"
                                  type="button"
                                >
                                  <a
                                    href={addGiftCardUploadTemplate}
                                    download="GiftCard_CSV_template.csv"
                                    className="text-white"
                                  >
                                    {t("Download CSV template")}
                                  </a>
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                        <div className="mt-3"></div>
                        <div className="col-12 d-flex justify-content-end my-4">
                          <Button
                            type="button"
                            className="mx-2"
                            color="secondary"
                            onClick={() => navigate(-1)}
                          >
                            {t("Cancel")}
                          </Button>

                          <Button
                            type="submit"
                            color="success"
                            disabled={isLoading}
                            className="w_fit_content"
                          >
                            {mode === "add" ? t("Add") : t("Save")}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddGiftCard
